import Vue from "vue";
import { mapGetters, mapMutations, mapState } from "vuex";

import moment from "moment";

import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridAction } from "@/components/child/grid/gridAction";
import { GridColors, GridColumn, GridColumnType, GridColumnWithoutSort } from "@/components/child/grid/gridColumn";
import { Component } from "@/decorators";
import PaginationPackModel from "@/models/auxiliar/paginationPackModel";
import EnumExtensions from "@/models/enum/extensions/enumExtensions";
import SituacoesVenda from "@/models/enum/situacoesVenda";
import NotaFiscalEmissaoEmLoteVendaModel from "@/models/notafiscaleletronica/notaFiscalEmissaoEmLoteVendaModel";
import PaginationModel from "@/models/paginationModel";
import UsuarioModel from "@/models/usuarioModel";
import ValidationErrorModel from "@/models/validationErrorModel";
import notaFiscalServicoEmissaoLoteService from "@/services/notaFiscal/emissaoEmLote/notaFiscalServicoEmissaoLoteService";
import tipoVendaService from "@/services/tipoVendaService";
import vendedorService from "@/services/vendedorService";
import { AppState, Getters, Mutations, SessionActions } from "@/store/store";
import { convertToDateInputString } from "@/utils/common/date";
import Delay from "@/utils/common/delay";

import "@/components/parent/crud/crud.scss";

@Component({
    components: {
        gridComponent,
        dateTimePickerComponent,
        comboComponent,
    },
    computed: {
        ...mapState({
            loadingPack: (state: AppState) => state.paginationStatus.loading,
            paginationParams: (state: AppState) => state.paginationStatus.paginationParams,
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["GET_IS_FRACIONAMENTO"] as Getters),
    },
    methods: mapMutations(["GET_PARAMS"] as Mutations),
})
export default class NotaFiscalServicoEmissaoEmLoteComponent extends Vue {
    loadingPack: boolean;
    paginationParams: PaginationPackModel;
    usuarioLogado: UsuarioModel;
    GET_PARAMS: (path: string) => Promise<void>;
    GET_IS_FRACIONAMENTO: () => Promise<boolean>;

    gridSelectedValues: Array<NotaFiscalEmissaoEmLoteVendaModel> = [];
    gridData: Array<NotaFiscalEmissaoEmLoteVendaModel> = [];
    private isFracionamento = false;

    get gridColumns(): Array<GridColumn> {
        return [
            new GridColumnWithoutSort("clienteNome", "Cliente", GridColumnType.String),
            new GridColumnWithoutSort("codigo", "Cód. Venda", GridColumnType.String),
            new GridColumnWithoutSort("dataAprovacao", "Data Venda", GridColumnType.DateTime),
            new GridColumnWithoutSort("dataEntrega", "Data de Entrega", GridColumnType.DateTime),
            new GridColumnWithoutSort("situacaoDescricao", "Situação", GridColumnType.String),
            new GridColumnWithoutSort("valorFinal", "Valor", GridColumnType.Money),
        ];
    }
    extraActions: Array<object> = [];
    extraActionsCancelar: Array<GridAction> = [];
    pageIndex = 1;
    pageSize = 20;
    total = 0;

    isHidden = false;
    situacao = null;
    situacoesVendaOptions = EnumExtensions.getNamesAndValuesOrderedByValues(SituacoesVenda);
    emissaoDe: Date = null;
    emissaoAte: Date = null;
    dataEntregaDe: Date = null;
    dataEntregaAte: Date = null;
    gridFilterKey = "";
    vendedorId = 0;
    vendedoresOptions: Array<{ value: number; text: string }> = [];
    dataFaturamentoDe: Date = null;
    dataFaturamentoAte: Date = null;
    tipoVendaId = 0;
    tipoVendaOptions: Array<Object> = [];

    private async load() {
        this.situacoesVendaOptions = EnumExtensions.getNamesAndValuesOrderedByValues(SituacoesVenda);
        if (!this.isFracionamento) {
            this.situacoesVendaOptions = this.situacoesVendaOptions.slice(0, 6);
        }
        this.situacoesVendaOptions = this.situacoesVendaOptions.filter(p => p["value"] > 1 && p["value"] !== 5);

        try {
            const data = await notaFiscalServicoEmissaoLoteService
                .list(
                    this.pageIndex,
                    this.pageSize,
                    convertToDateInputString(this.emissaoDe),
                    convertToDateInputString(this.emissaoAte),
                    convertToDateInputString(this.dataEntregaDe),
                    convertToDateInputString(this.dataEntregaAte),
                    this.situacao,
                    this.gridFilterKey,
                    this.vendedorId,
                    convertToDateInputString(this.dataFaturamentoDe),
                    convertToDateInputString(this.dataFaturamentoAte),
                    this.tipoVendaId,
                )
                .withLoading()
                .resolveWithJSON<PaginationModel<NotaFiscalEmissaoEmLoteVendaModel>>();

            this.gridData = data.list;
            this.total = data.total;
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;

            this.extraActions.splice(0);
            this.extraActionsCancelar.splice(0);

            for (let i = 0; i < this.gridData.length; i++) {
                const ultimaTentativaComFalha = this.gridData[i].ultimaEmissaoStatusEnvioDescricao.length > 0;

                this.extraActionsCancelar.push(
                    new GridAction(
                        "falhaEmissao",
                        ultimaTentativaComFalha ? "Ultima tentativa de emissão falhou" : "",
                        ultimaTentativaComFalha ? "fa fa-exclamation-circle" : "",
                        GridColors.LIGHTRED,
                    ),
                );
            }

            this.extraActions.push(this.extraActionsCancelar);
        } catch {}
    }

    private formatDateFilter(date: Date, dataFinal = false): string {
        if (!date) return "";
        if (dataFinal) {
            return moment(date).endOf("day").format("YYYY-MM-DDTHH:mm:ss");
        }
        return moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
    }

    private async loadVendedores() {
        try {
            const response = await vendedorService.comboCached(null);

            this.vendedoresOptions = response.map(u => ({
                value: u.value,
                text: u.text,
            }));
        } catch {}
    }

    private async loadTiposVenda() {
        try {
            this.tipoVendaOptions = await tipoVendaService.comboCached(this.usuarioLogado.franquiaId);
        } catch {}
    }

    private async emitirNotasLote() {
        try {
            const vendaIds = this.gridSelectedValues.map(v => v.vendaId);

            const data = await notaFiscalServicoEmissaoLoteService
                .emitirEmLoteByVendaIds(vendaIds)
                .withLoading()
                .resolveWithJSON<ValidationErrorModel[]>();

            if (data.length == 0) {
                this.$showSuccess(
                    "Sucesso",
                    "As notas foram enviadas a prefeitura, após 15 minutos serão feitas as consultas automaticamente para verificação do resultado das emissões.",
                );
            } else {
                for (let i = 0; i < data.length; i++) {
                    const element = data[i];
                    const venda = this.gridData.find(p => element.field == p.vendaId.toString());

                    await this.$showError(
                        "Erro",
                        `Não foi possível enviar a nota da venda ${venda?.codigo}: ${element.references}`,
                    );
                }
            }
        } catch {}

        this.load();
    }

    private onChangePage(pageIndex: number) {
        this.pageIndex = pageIndex;
        this.load();
    }

    private onChangePageSize(newPageSize: number) {
        this.pageIndex = 1;
        this.pageSize = newPageSize;
        this.load();
    }

    private onChangeFilterKey(filterKey: string) {
        this.gridFilterKey = filterKey;
        this.load();
    }

    private onSelectedValuesChanged(values: Array<NotaFiscalEmissaoEmLoteVendaModel>) {
        this.gridSelectedValues = values;
    }

    private async loadPagination() {
        await this.GET_PARAMS(this.$route.path);
        while (this.loadingPack) await Delay(50);

        if (this.paginationParams != null) {
            this.pageIndex = this.paginationParams.pageIndex;
            this.pageSize = this.paginationParams.pageSize;
            this.gridFilterKey = this.paginationParams.filterKey;
        }
    }

    private onExtraAction(name: string, notaFiscalEletronicaEmissaoLote: NotaFiscalEmissaoEmLoteVendaModel) {
        switch (name) {
            case "falhaEmissao":
                this.$router.push("/notafiscalsaida-edicao/" + notaFiscalEletronicaEmissaoLote.ultimaNotaFiscalId);
                break;
            default:
                break;
        }
    }

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);

        this.isFracionamento = await this.GET_IS_FRACIONAMENTO();

        await this.loadVendedores();
        await this.loadTiposVenda();

        Promise.all([this.loadPagination()])
            .withLoading()
            .then(() => {
                this.load();
            })
            .catch(() => {});
    }
}
